import { Home, Wallet, AlertTriangle, Clock } from "lucide-react"
import api from "../services/api"
import { LOCALHOST_API } from "../App"
import { useEffect, useMemo, useState } from "react"

export default function Dashboard() {
  const [units, setUnits] = useState([])
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [unitsRes, roomsRes] = await Promise.all([
          api.get(`${LOCALHOST_API}/api/units`),
          api.get(`${LOCALHOST_API}/api/rooms`),
        ])

        setUnits(unitsRes.data || [])
        setRooms(roomsRes.data || [])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // ---- Metrics (based on real data) ----
  const totalUnits = rooms.length // usually total rooms, not assigned units
  const occupiedUnits = useMemo(
    () => units.filter((u) => u.status === "Occupied").length,
    [units]
  )

  const occupancyPct = totalUnits
    ? Math.round((occupiedUnits / totalUnits) * 100)
    : 0
  const vacantPct = 100 - occupancyPct

  // These require a payments/rent model. If you don't have it yet, keep them 0.
  const rentCollected = 0
  const outstandingRent = 0
  const latePayments = 0
  const paidOnTime = 0

  if (loading) return <div className="p-6">Loading dashboard…</div>

  return (
    <div className="min-h-screen bg-slate-100 p-6 w-full max-w-300 mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="xl:col-span-1 bg-slate-900 rounded-2xl p-6 text-white">
          <p className="text-sm text-slate-400 mb-1">Total Units</p>
          <h2 className="text-3xl font-semibold mb-4">{totalUnits}</h2>
        </div>

        <div className="xl:col-span-2 bg-white rounded-2xl p-6">
          <h3 className="text-sm font-medium text-slate-500 mb-4">
            Statistics (This Month)
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Stat
              icon={Home}
              label="Occupied Units"
              value={occupiedUnits}
              trend=""
              positive
            />
            <Stat
              icon={Wallet}
              label="Rent Collected"
              value={`$${rentCollected.toLocaleString()}`}
              trend=""
              positive
            />
            <Stat
              icon={AlertTriangle}
              label="Outstanding Rent"
              value={`$${outstandingRent.toLocaleString()}`}
              trend=""
            />
            <Stat
              icon={Clock}
              label="Late Payments"
              value={latePayments}
              trend=""
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4">
            Occupancy Overview
          </h3>
          <Progress label="Occupied" value={occupancyPct} color="bg-cyan-500" />
          <Progress label="Vacant" value={vacantPct} color="bg-slate-300" />
        </div>

        <div className="bg-white rounded-2xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Payment Summary</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between">
              <span className="text-slate-500">Paid On Time</span>
              <span className="font-medium">{paidOnTime}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-slate-500">Late Payments</span>
              <span className="font-medium text-orange-500">
                {latePayments}
              </span>
            </li>
            <li className="flex justify-between">
              <span className="text-slate-500">Outstanding</span>
              <span className="font-medium text-red-500">
                ${outstandingRent.toLocaleString()}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

/* ---------------- Components ---------------- */

function Stat({ icon: Icon, label, value, trend, positive }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
      <div className="p-3 rounded-xl bg-slate-100 text-slate-700">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="font-semibold text-slate-900">{value}</p>
        {trend ? (
          <span
            className={`text-xs ${positive ? "text-green-500" : "text-red-500"}`}
          >
            {trend}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function Progress({ label, value, color }) {
  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  )
}
